import { useState } from 'react';
import { Check, Search, Stethoscope } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { IPD_CONSULT_URGENCY, type IpdConsultUrgency } from '@his/shared';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { FormSection } from '@/components/ui/form-screen';
import { Field, Input, Select, Skeleton, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useCareTeamCandidates } from '../../admission/queries';
import type { FieldsProps } from './form-props';
import type { ConsultDraft, ConsultTarget } from './order-form';

interface DepartmentRow {
  id: string;
  name: string;
}

const URGENCY_TONE: Record<IpdConsultUrgency, 'default' | 'warning' | 'destructive'> = { routine: 'default', urgent: 'warning', emergency: 'destructive' };

function useDepartments(branchId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['departments', 'branch', branchId ?? 'all'],
    queryFn: () => get<DepartmentRow[]>(`/org/departments${qs({ branchId })}`),
    enabled,
    staleTime: 600_000,
  });
}

export function ConsultFields({ draft, update, error, admission }: FieldsProps<ConsultDraft>) {
  const { can, user } = useAuth();
  const [search, setSearch] = useState('');
  const doctors = useCareTeamCandidates(admission.branchId, true);
  const departments = useDepartments(admission.branchId, can('department.read'));
  const departmentRows = departments.data ?? [];
  const offerDepartments = departmentRows.length > 0;
  const needle = search.trim().toLowerCase();
  const candidates = (doctors.data ?? []).filter((doctor) => doctor.id !== user?.id);
  const shown = needle ? candidates.filter((doctor) => `${doctor.name} ${doctor.designation ?? ''}`.toLowerCase().includes(needle)) : candidates;
  const target: ConsultTarget = offerDepartments ? draft.target : 'doctor';

  return (
    <FormSection title={tr('ipd', 'io_form_section_consult')} icon={Stethoscope}>
      {offerDepartments && (
        <div className="sm:col-span-2">
          <ChoiceButtons
            value={target}
            label={tr('ipd', 'io_form_consult_target')}
            onChange={(next) => update({ target: next })}
            options={[
              { value: 'doctor', label: tr('ipd', 'io_form_target_doctor') },
              { value: 'department', label: tr('ipd', 'io_form_target_department') },
            ]}
          />
        </div>
      )}
      {target === 'doctor' ? (
        <Field label={tr('ipd', 'io_form_target_doctor')} required error={error('targetDoctorId', 'targetDepartmentId')} className="sm:col-span-2">
          <div className="space-y-2">
            <div className="relative">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input className="ps-9" type="search" aria-label={tr('ipd', 'io_form_search_doctors')} placeholder={tr('ipd', 'io_form_search_doctors')} value={search} onChange={(event) => setSearch(event.target.value)} />
            </div>
            {doctors.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : shown.length === 0 ? (
              <p className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">{tr('ipd', 'io_form_no_doctors')}</p>
            ) : (
              <div role="radiogroup" aria-label={tr('ipd', 'io_form_target_doctor')} className="max-h-64 divide-y overflow-y-auto rounded-md border">
                {shown.map((doctor) => {
                  const selected = doctor.id === draft.targetDoctorId;
                  return (
                    <button
                      key={doctor.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => update({ targetDoctorId: doctor.id })}
                      className={cn('focus-ring flex w-full items-center gap-3 px-3 py-2 text-start text-sm transition-colors', selected ? 'bg-primary/10' : 'hover:bg-muted')}
                    >
                      <span className="min-w-0 flex-1">
                        <span className={cn('block truncate', selected && 'font-medium text-primary')}>{doctor.name}</span>
                        {doctor.designation && <span className="block truncate text-xs text-muted-foreground">{doctor.designation}</span>}
                      </span>
                      {selected && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </Field>
      ) : (
        <Field label={tr('ipd', 'io_form_department')} required error={error('targetDepartmentId', 'targetDoctorId')} className="sm:col-span-2">
          <Select placeholder={tr('ipd', 'io_form_choose_department')} value={draft.targetDepartmentId} onChange={(event) => update({ targetDepartmentId: event.target.value })}>
            {departmentRows.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <Field label={tr('ipd', 'io_form_specialty')} error={error('specialty')}>
        <Input maxLength={120} value={draft.specialty} onChange={(event) => update({ specialty: event.target.value })} />
      </Field>
      <Field label={tr('ipd', 'io_form_urgency')} error={error('urgency')}>
        <ChoiceButtons
          value={draft.urgency}
          label={tr('ipd', 'io_form_urgency')}
          onChange={(urgency) => update({ urgency })}
          options={IPD_CONSULT_URGENCY.map((urgency) => ({ value: urgency, label: tr('ipd', `io_form_urgency_${urgency}`), tone: URGENCY_TONE[urgency] }))}
        />
      </Field>
      <Field label={tr('ipd', 'io_form_question')} required error={error('question')} className="sm:col-span-2">
        <Textarea rows={3} maxLength={1000} value={draft.question} onChange={(event) => update({ question: event.target.value })} />
      </Field>
    </FormSection>
  );
}
